// AI 營運域(S23–S26)資料層。
//
// restful `/v3/admin/ai/*` 是**純 passthrough 白名單代理**(比照 `/v3/admin/fw/*`、
// `/v3/admin/pki/*`):它剝掉 `/v3/admin/ai` 前綴、把其餘 path 轉給 cs-copilot 的 admin
// 路由,並**原封不動回傳 cs 的裸 dict**(例:`{"traces":[...]}`),不是 restful 慣用的
// `{data}` envelope。因此 lib/api 的 `apiFetch`(會抽 `body.data`)不能用 —— 這裡自備
// `aiFetch`,回傳裸 JSON、不抽 `.data`。
//
// 認證沿用 console 既有機制:tokenStore 取 Bearer、401 時 refreshAccessToken 換發後重試
// 一次、config.clientId/clientKey 當 X-Client-Id/Key(CSRF gate)。錯誤時 cs 走 FastAPI
// `{"detail":"..."}`,restful 自身的守門(401/403)可能走 `{"error":{code,message}}`;
// 兩種都讀,一律丟出 lib/api 的 `ApiError`,讓 StateBlock 的 403/request_id 處理生效。
//
// Phase-1 端點對照(restful 前綴 `/v3/admin/ai` + 下列 path → cs admin handler):
//   GET   /traces                    → cs GET  /admin/traces
//   GET   /traces/{id}               → cs GET  /admin/traces/{id}
//   GET   /settings                  → cs GET  /admin/settings
//   PATCH /settings                  → cs PATCH/admin/settings
//   GET   /calibrate                 → cs GET  /admin/calibrate
//   POST  /try                       → cs POST /admin/try
//   GET   /handoffs                  → cs GET  /admin/handoffs
//   GET   /handoffs/{id}             → cs GET  /admin/handoffs/{id}
//   POST  /handoffs/{id}/claim       → cs POST /admin/handoffs/{id}/claim
//   POST  /handoffs/{id}/reply       → cs POST /admin/handoffs/{id}/reply
//   POST  /handoffs/{id}/close       → cs POST /admin/handoffs/{id}/close
//   GET   /kb-gaps                   → cs GET  /kb-gaps
//   POST  /rtbf                      → cs POST /admin/rtbf   (RTBF:刪某 owner 全部 AI 對話資料)
//   GET   /dsar                      → cs GET  /admin/dsar   (DSAR:匯出某 owner 全部 AI 對話資料)

import { config } from '../../config';
import { ApiError, refreshAccessToken, tokenStore } from '../../lib/api';
import { requestCommerceOperation, type CommerceOperation } from '../commerce/commerceApi';

const AI_PREFIX = '/v3/admin/ai';

type Query = Record<string, string | number | boolean | null | undefined>;

interface AiFetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

function buildAiUrl(path: string, query?: Query): string {
  const url = new URL(config.apiBase + AI_PREFIX + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/** cs `{"detail"}` 或 restful `{"error":{code,message}}` 都讀得出可讀訊息。 */
function readError(status: number, body: unknown, requestId: string | null): ApiError {
  const b = (body ?? {}) as { detail?: unknown; error?: { code?: string; message?: string } };
  if (b.error && (b.error.code || b.error.message)) {
    return new ApiError(status, b.error.code ?? `HTTP_${status}`, b.error.message ?? `HTTP ${status}`, requestId);
  }
  // FastAPI HTTPException.detail 通常是字串,也可能是 validation errors 陣列。
  let detail = '';
  if (typeof b.detail === 'string') detail = b.detail;
  else if (b.detail != null) detail = JSON.stringify(b.detail);
  return new ApiError(status, `HTTP_${status}`, detail || `HTTP ${status}`, requestId);
}

/**
 * 呼叫 restful `/v3/admin/ai/*` passthrough,回傳 cs 的**裸 JSON**(不抽 `.data`)。
 * 401 → refresh-and-retry 一次;第二次仍 401(或 refresh 失敗)清 token 並丟 ApiError(401)。
 * 4xx/5xx → 讀 cs/restful 錯誤格式丟 ApiError(403 由 ErrorState 特判為「無權限」)。
 */
export async function aiFetch<T>(path: string, opts: AiFetchOptions = {}, _retried = false): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client-Id': config.clientId,
    'X-Client-Key': config.clientKey,
  };
  const token = tokenStore.get();
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const res = await fetch(buildAiUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !_retried) {
    try {
      await refreshAccessToken();
    } catch {
      tokenStore.clear();
      throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', res.headers.get('X-Request-Id'));
    }
    return aiFetch<T>(path, opts, true);
  }

  const requestId = res.headers.get('X-Request-Id');
  const body = (await res.json().catch(() => ({}))) as unknown;

  if (res.status === 401) {
    tokenStore.clear();
    throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', requestId);
  }
  if (!res.ok) throw readError(res.status, body, requestId);

  return body as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// cs 回應形狀(據 cs-copilot/src/cs/{chat,observability,handoffs,settings,eval_scan}.py
// 之實際回傳 typing;勿臆測欄位)。cs 的時間戳是 Postgres timestamp 字串(非微秒)。
// ─────────────────────────────────────────────────────────────────────────────

/** answered / no_answer / out_of_scope / error(reply->>'status',可能為 null)。 */
export type AiTraceStatus = 'answered' | 'no_answer' | 'out_of_scope' | 'error' | (string & {});
/** 評價 rating:up / down(feedback.rating)。 */
export type AiRating = 'up' | 'down' | (string & {});

/** GET /traces 一列(observability.list_traces)。 */
export interface AiTraceRow {
  id: string;
  created_at: string;
  query: string;
  status: AiTraceStatus | null;
  latency_ms: number | null;
  conversation_id: string;
  rating: AiRating | null;
}
export interface AiTracesResponse {
  traces: AiTraceRow[];
}

/** 檢索片段(retrieved JSONB;只留 source/heading_path/score)。 */
export interface AiRetrievedHit {
  source: string | null;
  heading_path: string | null;
  score: number | null;
}
/** CSReply 的 source(命中片段驗證後)。 */
export interface AiReplySource {
  title: string;
  url: string;
}
/** reply JSONB = CSReply.model_dump = {status, answer, sources}。 */
export interface AiReply {
  status: AiTraceStatus;
  answer: string;
  sources: AiReplySource[];
}
/** 同對話各輪(get_trace.conversation_turns)。 */
export interface AiTraceTurn {
  id: string;
  query: string;
  status: AiTraceStatus | null;
  created_at: string;
}
/** GET /traces/{id}(observability.get_trace)。 */
export interface AiTraceDetail {
  id: string;
  created_at: string;
  conversation_id: string;
  query: string;
  retrieved: AiRetrievedHit[] | null;
  tool_calls: string[] | null;
  reply: AiReply | null;
  model: string | null;
  tokens: number | null;
  latency_ms: number | null;
  feedback: { rating: AiRating; comment: string | null; created_at: string } | null;
  conversation_turns: AiTraceTurn[];
}

/** GET/PATCH /settings(settings.get_settings)。 */
export interface AiSettings {
  retrieve_threshold: number;
  is_override: boolean;
  default: number;
  updated_at: string | null;
}

/** GET /calibrate(eval_scan.calibrate)。suggested 正/負樣本不足時為 null。 */
export interface AiCalibrate {
  suggested: number | null;
  pos: number[];
  neg: number[];
  n_cases: number;
}

/** POST /try(chat.admin_try;ephemeral,不落 traces)。 */
export interface AiTryResult {
  status: AiTraceStatus;
  answer: string;
  sources: AiReplySource[];
  retrieved: AiRetrievedHit[];
  top_score: number;
  threshold: number;
}

/** open / claimed / closed(handoffs.VALID_STATUS)。 */
export type AiHandoffStatus = 'open' | 'claimed' | 'closed' | (string & {});

/** GET /handoffs 一列(handoffs.list_handoffs;messages=訊息數)。 */
export interface AiHandoffRow {
  id: number;
  conversation_id: string;
  user_id: string | null;
  query: string | null;
  status: AiHandoffStatus;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  messages: number;
}
export interface AiHandoffsResponse {
  handoffs: AiHandoffRow[];
}

/** 往返訊息(handoff_messages)。 */
export interface AiHandoffMessage {
  id: number;
  sender: string; // staff | customer
  body: string;
  created_at: string;
}
/** GET /handoffs/{id}(handoffs.get_handoff;messages=陣列,且無 updated_at)。 */
export interface AiHandoffDetail {
  id: number;
  conversation_id: string;
  user_id: string | null;
  query: string | null;
  status: AiHandoffStatus;
  assigned_to: string | null;
  created_at: string;
  messages: AiHandoffMessage[];
}

/** GET /kb-gaps(observability.kb_gaps)。 */
export interface AiKbGap {
  query: string;
  count: number;
  last: string;
}
export interface AiKbGapsResponse {
  gaps: AiKbGap[];
}

// ── RTBF / DSAR(chat.admin_rtbf / admin_dsar → retention.purge_owner / export_owner)──────────
//
// owner 形如 `user:<uid>` / `external:<帳號>` / `app:<device>`(cs owner 綁定;retention.py）。
// purge_owner 一律刪 feedback/traces/conversations;owner 以 `user:` 起手才連帶刪 butler_memory/
// handoffs(故此兩欄為選填)。cs 回裸 dict,不是 restful `{data}` envelope。

/** POST /rtbf(purge_owner 各表刪除筆數)。 */
export interface AiRtbfResult {
  ok: boolean;
  owner: string;
  deleted: {
    feedback: number;
    traces: number;
    conversations: number;
    butler_memory?: number;
    handoffs?: number;
  };
}

/** DSAR 匯出一筆對話(export_owner.conversations)。 */
export interface AiDsarConversation {
  conversation_id: string;
  updated_at: string;
}
/** DSAR 匯出一筆 trace(export_owner.traces;reply=CSReply JSONB,可能為 null)。 */
export interface AiDsarTrace {
  id: string;
  conversation_id: string;
  query: string;
  reply: AiReply | null;
  created_at: string;
}
/** DSAR 匯出一筆評價(export_owner.feedback)。 */
export interface AiDsarFeedback {
  conversation_id: string;
  message_id: string;
  rating: AiRating;
  comment: string | null;
  created_at: string;
}
/** GET /dsar(export_owner:某 owner 的全部 AI 對話資料)。 */
export interface AiDsarExport {
  owner: string;
  conversations: AiDsarConversation[];
  traces: AiDsarTrace[];
  feedback: AiDsarFeedback[];
}

// ── 端點包裝 ──────────────────────────────────────────────────────────────────

export interface TracesFilter {
  status?: string;
  feedback?: string;
  q?: string;
  before?: string;
  limit?: number;
}

export function fetchTraces(f: TracesFilter, signal?: AbortSignal): Promise<AiTracesResponse> {
  return aiFetch<AiTracesResponse>('/traces', {
    query: { status: f.status, feedback: f.feedback, q: f.q, before: f.before, limit: f.limit },
    signal,
  });
}

export function fetchTraceDetail(id: string, signal?: AbortSignal): Promise<AiTraceDetail> {
  return aiFetch<AiTraceDetail>(`/traces/${encodeURIComponent(id)}`, { signal });
}

export function fetchSettings(signal?: AbortSignal): Promise<AiSettings> {
  return aiFetch<AiSettings>('/settings', { signal });
}

export function fetchCalibrate(signal?: AbortSignal): Promise<AiCalibrate> {
  return aiFetch<AiCalibrate>('/calibrate', { signal });
}

/** PATCH /settings。value=null 清除覆蓋、回 config 預設。 */
export function patchThreshold(retrieve_threshold: number | null): Promise<AiSettings> {
  return aiFetch<AiSettings>('/settings', { method: 'PATCH', body: { retrieve_threshold } });
}

export function tryAsk(message: string, visibility: 'internal' | 'public', signal?: AbortSignal): Promise<AiTryResult> {
  return aiFetch<AiTryResult>('/try', { method: 'POST', body: { message, visibility }, signal });
}

export function fetchHandoffs(status: string | undefined, signal?: AbortSignal): Promise<AiHandoffsResponse> {
  return aiFetch<AiHandoffsResponse>('/handoffs', { query: { status }, signal });
}

export function fetchHandoffDetail(id: number, signal?: AbortSignal): Promise<AiHandoffDetail> {
  return aiFetch<AiHandoffDetail>(`/handoffs/${id}`, { signal });
}

export function claimHandoff(id: number): Promise<{ ok: boolean }> {
  return aiFetch<{ ok: boolean }>(`/handoffs/${id}/claim`, { method: 'POST' });
}

export function replyHandoff(id: number, body: string): Promise<{ ok: boolean; message_id: number }> {
  return aiFetch<{ ok: boolean; message_id: number }>(`/handoffs/${id}/reply`, { method: 'POST', body: { body } });
}

export function closeHandoff(id: number): Promise<{ ok: boolean }> {
  return aiFetch<{ ok: boolean }>(`/handoffs/${id}/close`, { method: 'POST' });
}

export function fetchKbGaps(signal?: AbortSignal): Promise<AiKbGapsResponse> {
  return aiFetch<AiKbGapsResponse>('/kb-gaps', { signal });
}

/** 寫入時附帶的稽核脈絡。自 restful!404 起 reason/case_id 為必填(commerce operation 契約)。 */
export interface AiRtbfContext {
  reason: string;
  case_id: string;
}

/**
 * RTBF:刪某 owner 的全部 AI 對話資料(traces/handoffs/記憶…)。不可逆。
 *
 * ⚠️ 不再直打 `POST /v3/admin/ai/rtbf` —— 該路徑自 restful!404 起一律回
 * 409 `DUAL_CONTROL_REQUIRED`。RTBF 是不可逆的跨 owner 清除，屬受治理動作
 * (common/12 §8 D14④/⑤),必須走 maker/checker:本函式只**建立待覆核操作**,
 * 由另一位管理員在雙人覆核面板以密碼 step-up 核准後才真正執行。
 * 刪除筆數在 operation 完成後由 `result` 帶回(形狀＝AiRtbfResult)。
 */
export function requestAiRtbf(owner: string, ctx: AiRtbfContext): Promise<CommerceOperation> {
  return requestCommerceOperation('ai.rtbf', { owner }, { reason: ctx.reason, case_id: ctx.case_id });
}

/** DSAR:匯出某 owner 的全部 AI 對話資料。 */
export function aiDsar(owner: string, signal?: AbortSignal): Promise<AiDsarExport> {
  return aiFetch<AiDsarExport>('/dsar', { query: { owner }, signal });
}

// ── 顯示輔助(無 raw enum;中文 label) ────────────────────────────────────────

const TRACE_STATUS_LABEL: Record<string, string> = {
  answered: '已回答',
  no_answer: '查無知識',
  out_of_scope: '超出範圍',
  error: '錯誤',
};
export function traceStatusLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return TRACE_STATUS_LABEL[s] ?? s;
}
export function traceStatusTone(s: string | null | undefined): string {
  switch (s) {
    case 'answered':
      return 'pill-good';
    case 'no_answer':
      return 'pill-warn';
    case 'out_of_scope':
      return 'pill-neutral';
    case 'error':
      return 'pill-danger';
    default:
      return 'pill-neutral';
  }
}

export function ratingLabel(r: string | null | undefined): string {
  if (r === 'up') return '👍 正評';
  if (r === 'down') return '👎 負評';
  return '—';
}
export function ratingTone(r: string | null | undefined): string {
  if (r === 'up') return 'pill-good';
  if (r === 'down') return 'pill-danger';
  return 'pill-neutral';
}

const HANDOFF_STATUS_LABEL: Record<string, string> = {
  open: '待處理',
  claimed: '已認領',
  closed: '已結單',
};
export function handoffStatusLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return HANDOFF_STATUS_LABEL[s] ?? s;
}
export function handoffStatusTone(s: string | null | undefined): string {
  switch (s) {
    case 'open':
      return 'pill-warn';
    case 'claimed':
      return 'pill-info';
    case 'closed':
      return 'pill-neutral';
    default:
      return 'pill-neutral';
  }
}

export function senderLabel(sender: string): string {
  if (sender === 'staff') return '客服';
  if (sender === 'customer') return '顧客';
  return sender;
}

/** cs 的 Postgres timestamp 字串 → 本地時間(非微秒;不能用 formatTimestampUs)。 */
export function formatCsTime(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** 延遲毫秒 → 可讀。 */
export function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

// ─────────────────────────────────────────────────────────────────────────────
// S27 管家稽核(butler audit)—— restful `/v3/admin/ai/butler/*` passthrough → cs。
// 三支唯讀端點:主動卡 / 記憶 / 相機存取軌。全走 aiFetch(裸 dict,不抽 .data)。
//
//   GET /butler/cards    ?owner_uid=&status=&limit=  → {"cards":[…]}
//   GET /butler/memories ?owner_uid=&member_id=&category=&limit=  → {"memories":[…]}
//   GET /butler/vision   ?owner_uid=&limit=  → {"vision":[…]}
//
// ⚠️ 時間戳兩種格式並存:
//   - 主動卡 created_at / acted_at = **epoch 微秒**(用 formatTimestampUs,lib/format)。
//   - 記憶 / vision 的時間 = **Postgres timestamp 字串**(用 formatCsTime)。
// ─────────────────────────────────────────────────────────────────────────────

/** 主動卡種類(butler_card.kind)。 */
export type AiButlerCardKind = 'suggestion' | 'event_digest' | 'scene_offer' | 'tip' | (string & {});
/** 主動卡狀態(butler_card.status)。 */
export type AiButlerCardStatus = 'active' | 'acted' | 'dismissed' | (string & {});
/** 記憶類別(butler_memory.category)。 */
export type AiButlerMemoryCategory =
  | 'preference'
  | 'fact'
  | 'routine'
  | 'contact'
  | 'note'
  | 'warning'
  | (string & {});

/** 主動卡的一個動作(actions JSONB;形狀寬鬆,best-effort 顯示 label/type)。 */
export interface AiButlerCardAction {
  label?: string;
  type?: string;
  deep_link?: string;
  [k: string]: unknown;
}

/** GET /butler/cards 一列。created_at/acted_at = epoch 微秒。 */
export interface AiButlerCard {
  card_id: string;
  owner_uid: string;
  kind: AiButlerCardKind;
  title: string;
  body: string;
  actions: AiButlerCardAction[] | null;
  status: AiButlerCardStatus;
  created_at: number | string;
  /** 觸發事件 JSONB(可能 null)。 */
  source_event?: Record<string, unknown> | null;
  /** 觸發判斷分數(可能 null)。 */
  score?: number | null;
  /** 觸發判斷說明(可能 null)。 */
  reason?: string | null;
  /** 採納/忽略時間 epoch 微秒(可能 null)。 */
  acted_at?: number | string | null;
}
export interface AiButlerCardsResponse {
  cards: AiButlerCard[];
}

/** GET /butler/memories 一列。created_at = timestamp 字串。 */
export interface AiButlerMemory {
  memory_id: string;
  owner_uid: string;
  member_id: string | null;
  category: AiButlerMemoryCategory;
  content: string;
  is_pinned: boolean;
  /** 記憶來源觸發(可能 null)。 */
  trigger: string | null;
  created_at: string;
  source_conversation_id?: string | null;
  source_trace_id?: string | null;
}
export interface AiButlerMemoriesResponse {
  memories: AiButlerMemory[];
}

/** GET /butler/vision 一列(相機存取軌)。時間為 timestamp 字串。 */
export interface AiButlerVisionAccess {
  owner_uid: string;
  device_id: string;
  eid: string;
  source: string;
  event_ts: string;
  triggered_at: string;
}
export interface AiButlerVisionResponse {
  vision: AiButlerVisionAccess[];
}

export interface ButlerCardsParams {
  owner_uid?: string;
  status?: string;
  limit?: number;
}
export interface ButlerMemoriesParams {
  owner_uid?: string;
  member_id?: string;
  category?: string;
  limit?: number;
}
export interface ButlerVisionParams {
  owner_uid?: string;
  limit?: number;
}

/** GET /butler/cards —— 管家主動卡稽核(唯讀)。 */
export function butlerCards(p: ButlerCardsParams = {}, signal?: AbortSignal): Promise<AiButlerCardsResponse> {
  return aiFetch<AiButlerCardsResponse>('/butler/cards', {
    query: { owner_uid: p.owner_uid, status: p.status, limit: p.limit },
    signal,
  });
}

/** GET /butler/memories —— 管家記憶稽核(唯讀)。 */
export function butlerMemories(p: ButlerMemoriesParams = {}, signal?: AbortSignal): Promise<AiButlerMemoriesResponse> {
  return aiFetch<AiButlerMemoriesResponse>('/butler/memories', {
    query: { owner_uid: p.owner_uid, member_id: p.member_id, category: p.category, limit: p.limit },
    signal,
  });
}

/** GET /butler/vision —— 管家相機存取軌(唯讀)。 */
export function butlerVision(p: ButlerVisionParams = {}, signal?: AbortSignal): Promise<AiButlerVisionResponse> {
  return aiFetch<AiButlerVisionResponse>('/butler/vision', {
    query: { owner_uid: p.owner_uid, limit: p.limit },
    signal,
  });
}

// ── S27 顯示輔助(raw enum → 中文 label / tone) ──────────────────────────────

const BUTLER_CARD_KIND_LABEL: Record<string, string> = {
  suggestion: '建議',
  event_digest: '事件摘要',
  scene_offer: '情境提議',
  tip: '小提示',
};
export function butlerCardKindLabel(k: string | null | undefined): string {
  if (!k) return '—';
  return BUTLER_CARD_KIND_LABEL[k] ?? k;
}

const BUTLER_CARD_STATUS_LABEL: Record<string, string> = {
  active: '有效',
  acted: '已採納',
  dismissed: '已忽略',
};
export function butlerCardStatusLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return BUTLER_CARD_STATUS_LABEL[s] ?? s;
}
export function butlerCardStatusTone(s: string | null | undefined): string {
  switch (s) {
    case 'active':
      return 'pill-info';
    case 'acted':
      return 'pill-good';
    case 'dismissed':
      return 'pill-neutral';
    default:
      return 'pill-neutral';
  }
}

const BUTLER_MEMORY_CATEGORY_LABEL: Record<string, string> = {
  preference: '偏好',
  fact: '事實',
  routine: '作息',
  contact: '聯絡人',
  note: '備註',
  warning: '警示',
};
export function butlerMemoryCategoryLabel(c: string | null | undefined): string {
  if (!c) return '—';
  return BUTLER_MEMORY_CATEGORY_LABEL[c] ?? c;
}
export function butlerMemoryCategoryTone(c: string | null | undefined): string {
  return c === 'warning' ? 'pill-warn' : 'pill-neutral';
}
